// DELEGATE — the auto-evidence config read, its two cache commands and its
// frozen default all moved to `@heidi/evidence` at the `evidence/source` carve.
// What stays here is the one thing that could not cross: the app-resolved gate.
//
// The pre-move hook composed `(IS_TRANSCRIPT_CHIPS_ENABLED() ||
// IS_ASK_HEIDI_SHORTCUTS_ENABLED()) && !!token` inline, mixing two flag getters
// with a Rematch auth read inside a data hook. All three are app facts, none may
// enter a headless zone, and they resolve to one boolean here. This file stays
// BODIED (rather than dissolving to a re-export like the mutation did) precisely
// because that resolution is real work with a real failure mode: a frozen
// `enabled` would leave the query permanently disabled for a token that arrives
// after first render.
//
// The OR is EVI-2114 behaviour — config owns pinned shortcuts as well as chip
// categories, so killing transcript chips must not empty the pins — and it is
// preserved here rather than being modelled as two package inputs.
//
// The signature is unchanged (`select` stays the sole optional parameter), so
// every consumer and every `jest.mock('@/hooks/query/useQueryEvidenceConfig')`
// literal keeps resolving; the `enabled` parameter is an internal detail of the
// seam, not a new call-site obligation.
//
// `GET_EVIDENCE_CONFIG` and `DEFAULT_EVIDENCE_CONFIG` are DECLARED in
// `@heidi/evidence/contracts` and re-exported here name-intact so every importer
// keeps resolving. Never re-declare either here.
import {
  IS_ASK_HEIDI_SHORTCUTS_ENABLED,
  IS_TRANSCRIPT_CHIPS_ENABLED,
} from '@/features/flags/legacy-flags';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import type { AutoEvidenceConfig } from '@heidi/evidence/contracts';
import { useQueryEvidenceConfig as useQueryEvidenceConfigCore } from '@heidi/evidence/data';

export { DEFAULT_EVIDENCE_CONFIG, GET_EVIDENCE_CONFIG } from '@heidi/evidence/contracts';

// The two cache commands are PURE RE-EXPORTS: they were module-scope
// `reactQueryClient` calls, and they now run against the same singleton handed
// to `configureEvidenceData` at bootstrap. `useMutateTeamShortcut` reaches
// `invalidateEvidenceConfig` through this path and keeps doing so — that call
// stays app-side permanently, because `workspace` is FOUNDATIONAL and can take
// no product-domain value import.
export { getCachedEvidenceConfig, invalidateEvidenceConfig } from '@heidi/evidence/data';

/**
 * Hook to fetch user's Evidence configuration
 *
 * @param select - Optional selector to transform the data
 * @returns Query result with Evidence config
 */
export function useQueryEvidenceConfig<T = AutoEvidenceConfig>(
  select?: (data: AutoEvidenceConfig) => T
) {
  const { isAuthenticated } = useIsAuthenticated();
  // Config owns pinned shortcuts as well as chip categories — fetch whenever
  // either surface is enabled so killing transcript chips doesn't empty pins (EVI-2114).
  const shouldFetchConfig =
    IS_TRANSCRIPT_CHIPS_ENABLED() || IS_ASK_HEIDI_SHORTCUTS_ENABLED();

  return useQueryEvidenceConfigCore<T>(select, {
    enabled: shouldFetchConfig && isAuthenticated,
  });
}
